import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import CheckmarkIcon from 'packages/kakapo-ui-kit/assets/icons/CheckmarkIcon';
import Color from 'packages/kakapo-ui-kit/components/common/Color';
import ProgressBar from 'packages/kakapo-ui-kit/components/common/ProgressBar';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import GoalListItemCompetency from 'packages/kakapo-ui-kit/components/goals/list/GoalListItemCompetency';
import { InputResizableElement } from 'packages/kakapo-ui-kit/components/forms/controls/Field';

const Container = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	border-radius: 4px;
`;

const ChecklistTaskCheckboxLabel = styled.label`
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background: white;
	border: 1px solid ${({ theme }) => theme.colors.lightBlue};
	border-radius: 4px;
	transition: 100ms border ease-in, 100ms background-color ease-in;

	input:checked + & {
		border: 1px solid ${({ theme }) => theme.colors.primary};
		background-color: ${({ theme }) => theme.colors.primary};
	}
`;

function Checkbox(props: { name: string; checked: boolean; onClick: () => void }) {
	return (
		<Box flexShrink={0}>
			<input
				type="checkbox"
				id={`${props.name}.enabled`}
				checked={props.checked}
				onClick={props.onClick}
				style={{ display: 'none' }}
			/>
			<ChecklistTaskCheckboxLabel htmlFor={`${props.name}.enabled`}>
				<CheckmarkIcon />
			</ChecklistTaskCheckboxLabel>
		</Box>
	);
}

export default function FeedbackComposerGoalCard(props: {
	goal: graphql.Goal | graphql.FeedbackRequestGoal | graphql.GoalRevision;
	text?: string;
	selected?: boolean;
	onChange?: (selected: boolean, text?: string) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<>
			<Container p={3} mt={2}>
				<Flex alignItems="center">
					{'id' in props.goal && (
						<Box flexGrow={0} flexShrink={0} mr={3}>
							<Checkbox
								name={`goals.${props.goal.id}`}
								checked={props.selected ?? false}
								onClick={() => props.onChange?.(!props.selected)}
							/>
						</Box>
					)}
					<Box flexGrow={1} flexShrink={1}>
						<Box>
							<Color color="slateBlue">{localize(props.goal.title!).text}</Color>
						</Box>
						{props.goal.dueDate && (
							<Box>
								<Color color="lightBlueDark">
									{moment(props.goal.dueDate).format('DD-MM-YYYY')}
								</Color>
							</Box>
						)}
					</Box>
					<Box flexGrow={0} flexShrink={0} width={100} ml={3}>
						<ProgressBar progress={props.goal.progress} />
					</Box>
					<Box ml={2} flexGrow={0} flexShrink={0}>
						<Color color="slateBlue">{(props.goal.progress * 100).toFixed(0)}%</Color>
					</Box>
				</Flex>
				{props.goal.competency && (
					<Box mt={3}>
						<GoalListItemCompetency unspaced competency={props.goal.competency} />
					</Box>
				)}
			</Container>
			{props.selected && (
				<Box mt={2} mb={3}>
					<InputResizableElement
						lineHeight={30}
						minRows={1}
						maxRows={5}
						style={{ width: '100%' }}
						value={props.text ?? t('feedback.composer.goals.progressQuestion')}
						onChange={(event) =>
							props.onChange?.(props.selected ?? false, event.currentTarget.value)
						}
					/>
				</Box>
			)}
		</>
	);
}
